import { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Button from 'components/UI/Button';
import Select from 'components/UI/Select';
import { GA_REGISTRATION_ACTIONS, GA_REGISTRATION_LABELS } from 'constants/gaEvents';
import InlineError from 'components/UI/InlineError';
import useCollections from 'modules/clients/hooks/useCollections';
import { COUNTRIES } from 'constants/registration';
import StepHeader from './StepHeader';

const ClientIdentityResume = ({
  literals,
  data,
  onChange,
  onContinue,
  onBack,
  onEvent,
  headerStep,
  headerTotal,
  fieldErrors = {},
}) => {
  const collections = useCollections();

  const handleBack = () => {
    onEvent(
      GA_REGISTRATION_ACTIONS.EVENT,
      GA_REGISTRATION_LABELS.CAPTURE_AGAIN,
    );
    onBack();
  };

  useEffect(() => {
    if (data.countryOfBirth === 'MX' && !data.citizenship) {
      onChange({ ...data, citizenship: data.countryOfBirth });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.countryOfBirth]);

  const canContinue = useMemo(() => {
    return data.id && data.job && data.educationLevel && data.referral && data.countryOfBirth && data.citizenship;
  }, [data]);

  return (
    <div className='full-screen-container'>
      <div className='screen-content'>
        <StepHeader step={headerStep} total={headerTotal} onEvent={onEvent} />
        <p className='font-bold text-primary mb-6 text-xl'>{literals.validateData.validateData}</p>
        <p className='mb-4'>{literals.validateData.checkData}</p>
        <p className='font-bold text-primary text-sm mb-1'>{literals.validateData.id}</p>
        <p className='font-bold text-sm mb-2'>{data.id || '-'}</p>
        <p className='font-bold text-primary text-sm mb-1'>{literals.validateData.CURP}</p>
        <p className='font-bold text-sm mb-2'>{data.curp || '-'}</p>
        <p className='font-bold text-primary text-sm mb-1'>{literals.validateData.names}</p>
        <p className='font-bold text-sm mb-2'>{data.firstName || '-'}</p>
        <p className='font-bold text-primary text-sm mb-1'>{literals.validateData.lastNames}</p>
        <p className='font-bold text-sm mb-2'>{data.lastName || '-'}</p>
        <p className='font-bold text-primary text-sm mb-1'>{literals.validateData.birthDate}</p>
        <p className='font-bold text-sm mb-2'>{data.birthDate || '-'}</p>
        <p className='font-bold text-primary text-sm mb-1'>{literals.validateData.gender}</p>
        <p className='font-bold text-sm mb-6'>
          {(data.gender && data.idType && literals.validateData.genders[data.idType]?.[data.gender]) || data.gender || '-'}
        </p>
        <p className='font-bold text-lg text-primary mb-4'>{literals.validateData.additionalInfo}</p>
        <Select
          className='mb-4'
          options={Object.keys(COUNTRIES).map((country) => ({
            label: COUNTRIES[country],
            value: country,
          }))}
          label={literals.validateData.countryOfBirth}
          placeholder={literals.validateData.selectOneOption}
          value={data.countryOfBirth}
          error={fieldErrors.countryOfBirth || ''}
          onChange={(countryOfBirth) => onChange({ ...data, countryOfBirth })}
          disabled={!collections.data?.educationLevels?.length}
          canBeEmpty={false}
        />
        <Select
          className='mb-6'
          options={Object.keys(COUNTRIES).map((country) => ({
            label: COUNTRIES[country],
            value: country,
          }))}
          label={literals.validateData.citizenship}
          placeholder={literals.validateData.selectOneOption}
          value={data.citizenship}
          error={fieldErrors.citizenship || ''}
          onChange={(citizenship) => onChange({ ...data, citizenship })}
          disabled={!collections.data?.jobs?.length}
          canBeEmpty={false}
        />
        <p className='font-bold text-lg text-primary mb-4'>{literals.validateData.helpToKnowYou}</p>
        {collections.error && (
          <InlineError
            error={literals.validateData.collectionsError}
            className='mb-4'
          />
        )}
        <Select
          className='mb-4'
          options={collections.data?.jobs || []}
          label={literals.validateData.whatsYourJob}
          placeholder={literals.validateData.selectOneOption}
          value={data.job}
          error={fieldErrors.job || ''}
          onChange={(value) => onChange({ ...data, job: value })}
          disabled={!collections.data?.jobs?.length}
          loading={collections.loading}
          canBeEmpty={false}
        />
        <Select
          className='mb-4'
          options={collections.data?.educationLevels || []}
          label={literals.validateData.educationLevel}
          placeholder={literals.validateData.selectOneOption}
          value={data.educationLevel}
          error={fieldErrors.educationLevel || ''}
          onChange={(value) => onChange({ ...data, educationLevel: value })}
          disabled={!collections.data?.educationLevels?.length}
          loading={collections.loading}
          canBeEmpty={false}
        />
        <Select
          className='mb-4'
          options={collections.data?.referrals || []}
          label={literals.validateData.howDidYouHear}
          placeholder={literals.validateData.selectOneOption}
          value={data.referral}
          error={fieldErrors.referral || ''}
          onChange={(value) => onChange({ ...data, referral: value })}
          disabled={!collections.data?.referrals?.length}
          loading={collections.loading}
          canBeEmpty={false}
        />
      </div>
      <div className='screen-actions'>
        <Button onClick={onContinue} disabled={!canContinue}>{literals.common.next}</Button>
        <Button type='outline-primary' onClick={handleBack}>{literals.validateData.captureAgain}</Button>
      </div>
    </div>
  );
};

ClientIdentityResume.propTypes = {
  literals: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onEvent: PropTypes.func.isRequired,
  headerStep: PropTypes.number.isRequired,
  headerTotal: PropTypes.number.isRequired,
  fieldErrors: PropTypes.object,
};

export default ClientIdentityResume;
